import React, { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFetchDetail, usePutDetail } from "@hooks/useFetchDetail";
import { usePaginatedList } from "@hooks/usePaginatedList";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { openToast } from "@utils/toastStore";
import CsvDiscardsModalCst, {
  CsvDiscardsFile,
} from "@components/CsvDiscardsModalCst";
import StaticGroupWizard from "../GroupWizard/StaticGroupWizard";
import {
  buildInitialEnrollConfig,
  StaticGroupWizardData,
} from "../GroupWizard/types";
import { enrollGroupToLearningObjects } from "../enrollGroupToLearningObjects";
import { updateUserStaticGroups } from "../updateUserStaticGroups";
import { uploadStaticGroupCsv } from "../uploadStaticGroupCsv";

const MEMBERS_PAGE_SIZE = 1000;

const EditStaticGroup: React.FC = () => {
  const { id: corporateId = "", groupId = "" } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation("group-wizard-v2");
  const backTo = `/v2/corporate/${corporateId}/detail/users-groups?tab=static-groups`;
  const [submitting, setSubmitting] = useState(false);
  const [csvDiscards, setCsvDiscards] = useState<CsvDiscardsFile | null>(null);

  const { dataDetail, isLoading } = useFetchDetail(
    `${ServicesURL.staticGroups}/static`,
    groupId,
    PagesID["corporate.groups"],
  );

  const { trigger, setId } = usePutDetail(ServicesURL.staticGroups);
  useEffect(() => {
    if (groupId) setId(groupId);
  }, [groupId, setId]);

  const membersUrl =
    corporateId && groupId
      ? `${ServicesURL.staticGroups}/v3/${corporateId}/static/${groupId}/users`
      : null;
  const { items: members, isLoading: loadingMembers } = usePaginatedList(
    membersUrl,
    { page: 1, pageSize: MEMBERS_PAGE_SIZE },
    { responseKey: "response" },
  );

  const initialData = useMemo<StaticGroupWizardData | null>(
    () =>
      !isLoading && !loadingMembers && dataDetail && !Array.isArray(dataDetail)
        ? {
            name: dataDetail.name || "",
            description: dataDetail.description || "",
            userIds: (members || [])
              .map((m: any) => Number(m.user_id ?? m.id_user ?? m.id))
              .filter((idUser: number) => !Number.isNaN(idUser)),
            loSelections: [],
            enrollConfig: buildInitialEnrollConfig(),
            pendingCsvFile: null,
          }
        : null,
    [dataDetail, isLoading, members, loadingMembers],
  );

  const onComplete = async (data: StaticGroupWizardData) => {
    setSubmitting(true);
    try {
      await (trigger as any)({
        name: data.name || "",
        description: data.description || "",
      });
      let discards: CsvDiscardsFile | null = null;
      let csvUploaded = false;
      if (data.pendingCsvFile) {
        try {
          discards = await uploadStaticGroupCsv(groupId, data.pendingCsvFile);
          csvUploaded = true;
        } catch (error) {
          console.error(error);
          openToast({
            severity: "error",
            message: String(t("csvUpdateFailed")),
          });
        }
      }
      // TODO (BE Mancante): sostituire il ciclo di PUT per utente con
      // l'endpoint bulk (userIds sull'update del gruppo) quando disponibile.
      // L'upload del csv sostituisce l'intera membership del gruppo: in quel
      // caso gli utenti iniziali non sono piu' una baseline valida per il diff.
      const baselineIds = csvUploaded ? [] : (initialData?.userIds ?? []);
      const initialIds = new Set(baselineIds);
      const currentIds = new Set(data.userIds);
      const toAdd = data.userIds.filter((idUser) => !initialIds.has(idUser));
      const toRemove = baselineIds.filter((idUser) => !currentIds.has(idUser));
      const ops = [
        ...toAdd.map((idUser) =>
          updateUserStaticGroups(idUser, {
            addGroupIds: [Number(groupId)],
            removeGroupIds: [],
          }),
        ),
        ...toRemove.map((idUser) =>
          updateUserStaticGroups(idUser, {
            addGroupIds: [],
            removeGroupIds: [Number(groupId)],
          }),
        ),
      ];
      if (ops.length > 0) {
        const results = await Promise.allSettled(ops);
        const failed = results.filter((r) => r.status === "rejected").length;
        if (failed > 0) {
          openToast({
            severity: "error",
            message: String(
              t("usersUpdatePartial", {
                done: ops.length - failed,
                total: ops.length,
              }),
            ),
          });
        }
      }
      await enrollGroupToLearningObjects({
        corporateId,
        groupId,
        groupType: "STATIC",
        loSelections: data.loSelections,
        enrollType: data.enrollConfig.enrollType || "TOP",
        config: data.enrollConfig,
      });
      if (discards) {
        setCsvDiscards(discards);
        setSubmitting(false);
        return;
      }
      if (csvUploaded) {
        openToast({
          severity: "success",
          message: String(t("csvUpdateSuccess")),
        });
      }
      navigate(backTo);
    } catch (error) {
      setSubmitting(false);
      throw error;
    }
  };

  if (!initialData) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <CsvDiscardsModalCst
        open={!!csvDiscards}
        discards={csvDiscards}
        onClose={() => {
          setCsvDiscards(null);
          navigate(backTo);
        }}
      />
      <StaticGroupWizard
        initialData={initialData}
        backTo={backTo}
        onComplete={onComplete}
        isCompleting={submitting}
        title={t("titleEdit")}
        abandonMode="edit"
        groupId={groupId}
      />
    </>
  );
};

export default EditStaticGroup;
